import React, { useCallback, useRef, useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Chip, CircularProgress, Collapse, Button, Tooltip } from "@material-ui/core";
import CallMadeIcon from "@material-ui/icons/CallMade";
import CallReceivedIcon from "@material-ui/icons/CallReceived";
import PhoneMissedIcon from "@material-ui/icons/PhoneMissed";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import DescriptionIcon from "@material-ui/icons/Description";
import GraphicEqIcon from "@material-ui/icons/GraphicEq";
import AutoAwesomeIcon from "@material-ui/icons/EmojiObjects";
import PlaylistAddCheckIcon from "@material-ui/icons/PlaylistAddCheck";
import { useVoice } from "../../context/Voice/VoiceContext";
import api from "../../services/api";
import * as apiVoice from "../../services/apiVoice";
import { i18n } from "../../translate/i18n";
import QuickChargeButton from "../QuickChargeButton";

/**
 * A voice call as a NATIVE timeline event. Collapsed it is a clean centered
 * "system event" chip (📞 שיחה יוצאת · 27 שניות · הוקלטה) styled like the date
 * dividers; expanded it uses the exact ContactDrawer section DNA (surface card,
 * hairline border, green-gradient icon pills) so it reads as part of Whapiz,
 * not a bolted-on module. Recording is always available; transcript + AI are
 * on-demand (cost guard). Additive — consumes the enriched CallSession from
 * GET /voice/contacts/:id/calls; never touches the WhatsApp message path.
 */
const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const surface = dark ? "#131614" : "#ffffff";
  const surfaceSubtle = dark ? "rgba(255,255,255,0.05)" : "#f6faf7";
  const hair = dark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)";
  const textPrimary = dark ? "#e6f7ec" : "#0B0D0C";
  const textMuted = dark ? "rgba(230,247,236,0.55)" : "#5f6f6a";
  const brand = dark ? "#7df0a8" : "#128c7e";
  const iconPillBg = dark
    ? "linear-gradient(135deg, rgba(46,227,122,0.18), rgba(18,140,126,0.16))"
    : "linear-gradient(135deg, rgba(37,211,102,0.14), rgba(18,140,126,0.10))";
  return {
    wrap: { display: "flex", justifyContent: "center", margin: "10px 0 4px" },
    pill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
      maxWidth: "90%",
      cursor: "pointer",
      // Flatter, WhatsApp-style system-event chip — sits IN the message flow,
      // not as a raised card on top of it. No shadow; hairline tint only.
      background: dark ? "rgba(255,255,255,0.035)" : "rgba(15,23,42,0.035)",
      border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)"}`,
      borderRadius: 14,
      padding: "5px 12px",
      fontSize: 12.5,
      fontWeight: 500,
      color: textMuted,
      transition: "background .15s ease",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.06)",
      },
    },
    icon: { fontSize: 17 },
    iconOut: { color: dark ? "#2ee37a" : "#128c7e" },
    iconIn: { color: dark ? "#7fb4ff" : "#1565c0" },
    iconMissed: { color: dark ? "#ff8a80" : "#c62828" },
    sep: { opacity: 0.4, margin: "0 1px" },
    recDot: { color: "#c62828", fontSize: 9, verticalAlign: "middle", marginInlineEnd: 2 },
    chevron: { fontSize: 17, opacity: 0.5, transition: "transform .2s ease" },
    chevronOpen: { transform: "rotate(180deg)" },

    panelWrap: { display: "flex", justifyContent: "center" },
    panel: {
      width: "92%",
      maxWidth: 460,
      marginTop: 6,
      marginBottom: 4,
      background: surface,
      border: `1px solid ${hair}`,
      borderRadius: 14,
      padding: 14,
      color: textPrimary,
      boxShadow: dark ? "none" : "0 1px 2px rgba(15,23,42,0.05)",
    },
    metaRow: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 12, color: textMuted, marginBottom: 4 },
    section: { marginTop: 12, display: "flex", flexDirection: "column", gap: 8 },
    sectionHeader: { display: "flex", alignItems: "center", gap: 9 },
    iconPill: {
      width: 28,
      height: 28,
      borderRadius: 9,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: iconPillBg,
      color: brand,
      flexShrink: 0,
      "& svg": { fontSize: 16 },
    },
    sectionTitle: { fontSize: "0.8rem", fontWeight: 700, color: textPrimary, letterSpacing: 0.2 },
    box: {
      fontSize: 13,
      lineHeight: 1.55,
      background: surfaceSubtle,
      border: `1px solid ${hair}`,
      borderRadius: 10,
      padding: "8px 10px",
      whiteSpace: "pre-wrap",
      maxHeight: 220,
      overflowY: "auto",
      color: textPrimary,
    },
    audio: { width: "100%", height: 38 },
    chips: { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 },
    actionsRow: {
      display: "flex",
      gap: 8,
      alignItems: "center",
      flexWrap: "wrap",
      marginTop: 14,
      paddingTop: 12,
      borderTop: `1px solid ${hair}`,
    },
    outcomeChip: { cursor: "pointer" },
    muted: { color: textMuted, fontSize: 12.5 },
  };
});

const STATUS_HE = {
  completed: "voiceTimeline.statusCompleted",
  no_answer: "voiceTimeline.statusNoAnswer",
  busy: "voiceTimeline.statusBusy",
  failed: "voiceTimeline.statusFailed",
  active: "voiceTimeline.statusActive",
  ringing: "voiceTimeline.statusRinging",
  queued: "voiceTimeline.statusQueued",
};
const OUTCOME_HE = {
  sale: "voiceTimeline.outcomeSale",
  follow_up: "voiceTimeline.outcomeFollowUp",
  support: "voiceTimeline.outcomeSupport",
  payment: "voiceTimeline.outcomePayment",
  complaint: "voiceTimeline.outcomeComplaint",
  no_answer: "voiceTimeline.outcomeNoAnswer",
};
const SENTIMENT_HE = { positive: "voiceTimeline.sentimentPositive", neutral: "voiceTimeline.sentimentNeutral", negative: "voiceTimeline.sentimentNegative", urgent: "voiceTimeline.sentimentUrgent" };
const INTENT_HE = { sales: "voiceTimeline.intentSales", support: "voiceTimeline.intentSupport", payment: "voiceTimeline.intentPayment", complaint: "voiceTimeline.intentComplaint", information: "voiceTimeline.intentInformation" };
// Maps hold i18n KEYS (module scope runs once); translate at render time.
const T = (map, key) => (map[key] ? i18n.t(map[key]) : key);
const OUTCOMES = ["sale", "follow_up", "support", "payment", "complaint", "no_answer"];

const fmtDur = (s) => {
  const n = Number(s) || 0;
  if (n >= 60) return `${Math.floor(n / 60)} ${i18n.t("voiceTimeline.minShort")} ${String(n % 60).padStart(2, "0")} ${i18n.t("voiceTimeline.secShort")}`;
  return `${n} ${i18n.t("voiceTimeline.seconds")}`;
};
const fmtTime = (d) => {
  try {
    return new Date(d).toLocaleString("he-IL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const VoiceCallTimelineItem = ({ call, ticket }) => {
  const classes = useStyles();
  const { openOutcomeFor } = useVoice();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [transcript, setTranscript] = useState(call.transcript && call.transcript.text ? call.transcript.text : null);
  const [analysis, setAnalysis] = useState(call.analysis || null);
  const [working, setWorking] = useState(null);
  const [outcome, setOutcome] = useState(call.outcome || null);

  const isOut = call.direction !== "inbound";
  const missed = ["no_answer", "busy", "failed"].includes(call.status);
  const rec = call.recording;
  const hasRecording = rec && rec.status === "stored";
  const when = call.startedAt || call.createdAt;
  const CallIcon = missed ? PhoneMissedIcon : isOut ? CallMadeIcon : CallReceivedIcon;
  const title = isOut ? i18n.t("voiceTimeline.outgoing") : i18n.t("voiceTimeline.incoming");

  const toggleOpen = useCallback(() => {
    setOpen((o) => {
      const next = !o;
      if (next && rootRef.current) {
        // Keep the just-opened panel comfortably in view instead of letting it
        // shove the surrounding messages around unpredictably.
        setTimeout(() => {
          rootRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 230);
      }
      return next;
    });
  }, []);

  const loadAudio = useCallback(async () => {
    if (audioUrl || !hasRecording) return;
    setLoadingAudio(true);
    try {
      const { data } = await api.get(apiVoice.recordingUrl(rec.id), { responseType: "blob" });
      setAudioUrl(URL.createObjectURL(data));
    } catch (err) {
      /* still processing */
    } finally {
      setLoadingAudio(false);
    }
  }, [audioUrl, hasRecording, rec]);

  // Release the object URL for the (multi-MB) recording blob when it changes or
  // the row unmounts — otherwise the blob stays pinned in memory for the whole
  // session (audit: fe leak).
  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const waitForTranscript = useCallback(async () => {
    for (let i = 0; i < 14; i++) {
      // eslint-disable-next-line no-await-in-loop
      const t = await apiVoice.getCallTranscript(call.id).catch(() => null);
      if (t && t.status === "done" && t.text) return t.text;
      // eslint-disable-next-line no-await-in-loop
      await sleep(3000);
    }
    return null;
  }, [call.id]);

  const doTranscribe = useCallback(async () => {
    if (working) return;
    setWorking("tr");
    try {
      const text = await waitForTranscript();
      setTranscript(text || i18n.t("voiceTimeline.transcriptNotFound"));
    } finally {
      setWorking(null);
    }
  }, [working, waitForTranscript]);

  const doAnalyze = useCallback(async () => {
    if (working) return;
    setWorking("ai");
    try {
      const text = transcript || (await waitForTranscript());
      if (text && !transcript) setTranscript(text);
      const a = await apiVoice.analyzeCall(call.id).catch(() => null);
      if (a) setAnalysis(a);
    } finally {
      setWorking(null);
    }
  }, [working, transcript, waitForTranscript, call.id]);

  const tagOutcome = useCallback(
    async (key) => {
      setOutcome(key);
      try {
        await apiVoice.setCallOutcome(call.id, key);
      } catch (err) {
        /* keep optimistic */
      }
    },
    [call.id]
  );

  return (
    <div ref={rootRef}>
      <div className={classes.wrap}>
        <div className={classes.pill} onClick={toggleOpen}>
          <CallIcon className={`${classes.icon} ${missed ? classes.iconMissed : isOut ? classes.iconOut : classes.iconIn}`} />
          <span>{title}</span>
          <span className={classes.sep}>•</span>
          <span>{missed ? T(STATUS_HE, call.status) : call.durationSec > 0 ? fmtDur(call.durationSec) : T(STATUS_HE, call.status)}</span>
          {hasRecording && (
            <>
              <span className={classes.sep}>•</span>
              <span>
                <FiberManualRecordIcon className={classes.recDot} />
                {i18n.t("voiceTimeline.recorded")}
              </span>
            </>
          )}
          {outcome && <Chip size="small" color="primary" label={T(OUTCOME_HE, outcome)} />}
          <KeyboardArrowDownIcon className={`${classes.chevron} ${open ? classes.chevronOpen : ""}`} />
        </div>
      </div>

      <Collapse in={open} className={classes.panelWrap} timeout={240} unmountOnExit>
        <div className={classes.panel}>
          <div className={classes.metaRow}>
            <span>{fmtTime(when)}</span>
            <span className={classes.sep}>•</span>
            <span>{T(STATUS_HE, call.status)}</span>
            {call.durationSec > 0 && (
              <>
                <span className={classes.sep}>•</span>
                <span>⏱ {fmtDur(call.durationSec)}</span>
              </>
            )}
            {call.user && <span>· {call.user.name}</span>}
          </div>

          {/* Recording */}
          {hasRecording && (
            <div className={classes.section}>
              <div className={classes.sectionHeader}>
                <span className={classes.iconPill}>
                  <GraphicEqIcon />
                </span>
                <span className={classes.sectionTitle}>{i18n.t("voiceTimeline.recording")}</span>
              </div>
              {audioUrl ? (
                <audio className={classes.audio} src={audioUrl} controls preload="none" />
              ) : (
                <Button
                  size="small"
                  startIcon={loadingAudio ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                  onClick={loadAudio}
                  disabled={loadingAudio}
                  style={{ alignSelf: "flex-start" }}
                >
                  {i18n.t("voiceTimeline.listenRecording")}
                </Button>
              )}
            </div>
          )}

          {/* AI analysis */}
          {analysis && analysis.summary && (
            <div className={classes.section}>
              <div className={classes.sectionHeader}>
                <span className={classes.iconPill}>
                  <AutoAwesomeIcon />
                </span>
                <span className={classes.sectionTitle}>{i18n.t("voiceTimeline.aiAnalysis")}</span>
              </div>
              <div className={classes.box}>
                {analysis.summary}
                <div className={classes.chips}>
                  {analysis.intent && <Chip size="small" variant="outlined" label={T(INTENT_HE, analysis.intent)} />}
                  {analysis.sentiment && <Chip size="small" variant="outlined" label={T(SENTIMENT_HE, analysis.sentiment)} />}
                </div>
              </div>
            </div>
          )}

          {/* Transcript */}
          {transcript && (
            <div className={classes.section}>
              <div className={classes.sectionHeader}>
                <span className={classes.iconPill}>
                  <DescriptionIcon />
                </span>
                <span className={classes.sectionTitle}>{i18n.t("voiceTimeline.transcript")}</span>
              </div>
              <div className={classes.box}>{transcript}</div>
            </div>
          )}

          {/* On-demand triggers */}
          {hasRecording && (!transcript || !analysis) && (
            <div className={classes.section}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {!transcript && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={working === "tr" ? <CircularProgress size={14} /> : <DescriptionIcon />}
                    onClick={doTranscribe}
                    disabled={!!working}
                  >
                    {working === "tr" ? i18n.t("voiceTimeline.transcribing") : i18n.t("voiceTimeline.createTranscript")}
                  </Button>
                )}
                {!analysis && (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={working === "ai" ? <CircularProgress size={14} style={{ color: "#fff" }} /> : <AutoAwesomeIcon />}
                    onClick={doAnalyze}
                    disabled={!!working}
                  >
                    {working === "ai" ? i18n.t("voiceTimeline.analyzing") : i18n.t("voiceTimeline.analyzeCall")}
                  </Button>
                )}
              </div>
            </div>
          )}

          {!hasRecording && <div className={classes.muted} style={{ marginTop: 10 }}>{i18n.t("voiceTimeline.noRecording")}</div>}

          {/* Sales actions */}
          <div className={classes.actionsRow}>
            {OUTCOMES.map((k) => (
              <Chip
                key={k}
                size="small"
                className={classes.outcomeChip}
                variant={outcome === k ? "default" : "outlined"}
                color={outcome === k ? "primary" : "default"}
                label={T(OUTCOME_HE, k)}
                onClick={() => tagOutcome(k)}
              />
            ))}
            {ticket && (
              <Tooltip title={i18n.t("voiceTimeline.quickCharge")}>
                <span>
                  <QuickChargeButton ticket={ticket} />
                </span>
              </Tooltip>
            )}
            {openOutcomeFor && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PlaylistAddCheckIcon />}
                onClick={() => openOutcomeFor(call)}
              >
                {i18n.t("voiceTimeline.followUpAction")}
              </Button>
            )}
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export default VoiceCallTimelineItem;
